import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { Observable } from 'rxjs';

/**
 * Servicio mejorado para la gestión de diálogos
 * Proporciona métodos para abrir y cerrar diálogos de manera segura
 * evitando posibles problemas de memoria y diálogos huérfanos
 */
@Injectable({
  providedIn: 'root'
})
export class EnhancedDialogService {
  constructor(private dialog: MatDialog) {}

  /**
   * Abre un diálogo con manejo seguro de resultados y limpieza previa
   * @param component Componente a mostrar en el diálogo
   * @param config Configuración del diálogo
   * @returns Observable con el resultado del diálogo
   */
  openDialog<T, R = any>(component: ComponentType<T>, config: any): Observable<R> {
    // Limpiar posibles overlays huérfanos antes de abrir
    this.cleanupOrphanedOverlays();
    
    // Abrir el diálogo
    const dialogRef = this.dialog.open(component, config);
    
    // Devolver el observable de afterClosed
    return dialogRef.afterClosed();
  }

  /**
   * Cierra un diálogo específico con un resultado opcional
   * @param dialogRef Referencia al diálogo
   * @param result Resultado opcional para devolver
   */
  closeDialog<R>(dialogRef: MatDialogRef<any>, result?: R): void {
    if (dialogRef) {
      dialogRef.close(result);
    }
  }

  /**
   * Limpia overlays huérfanos sin cerrar todos los diálogos activos
   * Esta es una alternativa más segura a usar dialog.closeAll()
   */
  private cleanupOrphanedOverlays(): void {
    try {
      // Identificar y limpiar solo overlays huérfanos
      const orphanedBackdrops = document.querySelectorAll('.cdk-overlay-backdrop:not(.cdk-overlay-backdrop-showing)');
      orphanedBackdrops.forEach(backdrop => {
        backdrop.parentNode?.removeChild(backdrop);
      });

      // Limpiar clases globales que puedan causar problemas
      if (document.querySelectorAll('.cdk-overlay-container .cdk-overlay-backdrop').length === 0) {
        document.body.classList.remove('cdk-global-scrollblock');
      }
    } catch (error) {
      console.error('Error al limpiar overlays huérfanos:', error);
    }
  }
}
